/**
 * GithubのWikiを元にフロントエンド側から利用するAPIの定義を行っている
 * https://github.com/cuon-inc/ana-shower-lounge/wiki/Rails-%E3%81%A8-React%E3%81%AEAPI/
 *
 * TODO: wikiはまだ更新され続けるので、fixするまでは以下の定義も合わせて更新していく
 */

import type { InitialLoginPattern } from '@/constants/auth';
import type { SpaceType } from '@/constants/space';
import type { TodayAvailabilityResponse } from '@/constants/todayAvailability';
import type { MembershipType, UserRole } from '@/constants/users';
import type { RetryConfig } from '@/utils/retry';

// 実行時に参照するためのAPI定義
export const API_CONFIG = {
  GET: {
    UserMe: { path: '/api/users/me' },
    Reservation: { path: '/' },
    SpaceAvailability: { path: '/' },
    ReservationHistory: { path: '/api/spaces/reservations' },
    ReservationDetail: { path: '/api/spaces/reservations/:id' },
    SpaceList: { path: '/' },
    ShowerTimeSlots: {
      path: '/api/spaces/shared_device/available_shower_rooms',
    },
    ShowerRoomAvailableTimeSlots: {
      path: '/api/spaces/available_shower_rooms',
    },
    PrivateRoomAvailableTimeSlots: {
      path: '/api/spaces/available_private_rooms',
    },
    AreasAirports: { path: '/api/airports' },
    AreasTerminals: { path: '/api/terminals' },
    AreasLounges: { path: '/api/lounges' },
    LoungeNames: { path: '/api/lounge_names' },
    StaffReservationSearch: { path: '/api/spaces/staff/reservations/search' },
    // 本日の空室状況（認証不要）
    TodayAvailability: { path: '/api/public/today_availability' },
  },
  POST: {
    PasswordResetEmail: { path: '/api/password/reset_request' },
    StaffBulkCancelReservations: {
      path: '/api/spaces/staff/reservations/bulk_cancel',
    },
    UserLogin: { path: '/api/auth/session' },
    StaffLogin: { path: '/api/auth/staff/session' },
    SharedDeviceLogin: { path: '/api/auth/shared_device/session' },
    UserRegistration: { path: '/api/auth/registration' },
    UserRegistrationComplete: { path: '/api/auth/activation' },
    CreateContract: { path: '/api/auth/no_workhub_customer_registration' },
    TokenRefresh: {
      path: '/api/auth/refresh',
    },
    CreateSharedDeviceReservation: {
      path: '/api/spaces/shared_device/reservations',
      retry: { enabled: true },
    },
    CreateReservation: { path: '/api/spaces/reservations' },
  },
  PUT: {
    PasswordResetComplete: { path: '/api/password' },
  },
  DELETE: {
    CancelReservation: { path: '/api/spaces/reservations/:id' },
  },
} as const satisfies Record<
  string,
  Record<string, { path: string; retry?: RetryConfig }>
>;

// 型情報のためのAPI定義
export type API = {
  GET: {
    //ユーザー情報取得
    UserMe: {
      path: '/api/users/me';
      parameters: Record<string, never>;
      response: {
        data: {
          email: string;
          family_name_jp: string;
          first_name_jp: string;
          membership_type: 'ana_lounge' | 'suite_lounge';
          role: 'general' | 'staff' | 'admin';
        };
      };
      error: CommonError;
    };
    //予約情報取得
    Reservation: {
      path: '/';
      parameters: {
        reservation_no: string;
      };
      response: {
        space_code: string;
        start_datetime: string;
        end_datetime: string;
        pass_code: string;
      };
      error: CommonError;
    };
    //任意スペースの予約状況確認
    SpaceAvailability: {
      path: '/';
      parameters: {
        date: string;
      };
      response: Array<{
        date: string;
        start_time: string;
        end_time: string;
      }>;
      error: CommonError;
    };
    //予約履歴一覧取得
    ReservationHistory: {
      path: '/api/spaces/reservations';
      parameters: {
        space_type?: 'shower_room' | 'private_room';
      };
      response: Array<{
        id: number;
        date: string;
        start_time: string;
        end_time: string;
        status: 'scheduled' | 'cancelled';
      }>;
      error: CommonError;
    };
    //予約詳細取得
    ReservationDetail: {
      path: '/api/spaces/reservations/:id';
      parameters: {
        id: number;
      };
      response: {
        reservation: API['POST']['CreateReservation']['response']['reservation'];
      };
      error: CommonError;
    };
    //スペース一覧取得
    SpaceList: {
      path: '/';
      parameters: {
        email: string;
        password: string;
      };
      response: unknown; // レスポンス詳細未定
      error: CommonError;
    };
    //シャワールーム予約可能時間帯取得（共有端末）
    ShowerTimeSlots: {
      path: '/api/spaces/shared_device/available_shower_rooms';
      parameters: {
        area_id?: number;
      };
      response: {
        date: string;
        time_slots: Array<{
          time: string;
          date: string;
          datetime: string;
          available: boolean;
          displayable: boolean;
        }>;
      };
      error: CommonError;
    };
    //シャワールーム予約可能時間帯取得（通常導線）
    ShowerRoomAvailableTimeSlots: {
      path: '/api/spaces/available_shower_rooms';
      parameters: {
        area_ids: number[];
        date: string;
      };
      response: {
        date: string;
        time_slots: Array<{
          time: string; // "HH:MM" 形式
          date: string; // "YYYY-MM-DD" 形式
          datetime: string; // ISO8601 形式
          available: boolean;
          displayable: boolean;
        }>;
      };
      error: CommonError;
    };
    //防音個室ブース予約可能時間帯取得（通常導線）
    PrivateRoomAvailableTimeSlots: {
      path: '/api/spaces/available_private_rooms';
      parameters: {
        area_ids: number[];
        date: string;
      };
      response: {
        date: string;
        remaining_minutes: number; // 残り利用可能時間（分）
        has_active_reservation: boolean; // 未完了の予約があるかどうか
        spaces: Array<{
          space_id: number;
          space_no: number;
          space_name: string;
          floor: number;
          time_slots: Array<{
            time: string; // "HH:MM" 形式
            datetime: string; // ISO8601 形式
            available: boolean;
            displayable: boolean;
          }>;
        }>;
      };
      error: CommonError;
    };
    //空港+便種別リスト取得
    AreasAirports: {
      path: '/api/airports';
      parameters: {
        space_type: string;
      };
      response: {
        data: Array<{
          airport: string;
          flight_type: string;
        }>;
      };
      error: CommonError;
    };
    //ターミナルリスト取得
    AreasTerminals: {
      path: '/api/terminals';
      parameters: {
        space_type: string;
        airport?: string;
        flight_type?: string;
      };
      response: {
        data: string[];
      };
      error: CommonError;
    };
    //ラウンジリスト取得（全パラメータ必須）
    AreasLounges: {
      path: '/api/lounges';
      parameters: {
        space_type: string;
        airport: string;
        flight_type: string;
        terminal: string;
      };
      response: {
        data: Array<{
          lounge: string;
          area_ids: number[];
          accessible: boolean;
        }>;
      };
      error: CommonError;
    };
    //ラウンジ名一覧取得（スタッフ用）
    LoungeNames: {
      path: '/api/lounge_names';
      parameters: {
        space_type: string;
        airport?: string;
        flight_type?: string;
        terminal?: string;
      };
      response: {
        data: string[];
      };
      error: CommonError;
    };
    //スタッフ用予約検索
    StaffReservationSearch: {
      path: '/api/spaces/staff/reservations/search';
      parameters: {
        space_type: string;
        airport?: string;
        flight_type?: string;
        terminal?: string;
        lounge?: string;
        search_date?: string;
        starts_at_hour?: string;
        starts_at_minute?: string;
        ends_at_hour?: string;
        ends_at_minute?: string;
        reservation_no?: string;
        name?: string;
        page?: number;
        per_page?: number;
      };
      response: {
        reservations: Array<{
          reservation_no: string;
          last_name: string;
          first_name: string;
          email: string;
          starts_at: string;
          ends_at: string;
          airport: string;
          terminal: string;
          flight_type: string;
          lounge: string;
          space_no: string | null;
          pass_code: string;
          status: 'scheduled' | 'cancelled';
          is_kiosk: boolean;
        }>;
        pagination: {
          total_count: number;
          total_pages: number;
          current_page: number;
          per_page: number;
        };
      };
      error: CommonError;
    };
    //本日の空室状況取得（認証不要）
    TodayAvailability: {
      path: '/api/public/today_availability';
      parameters: {
        space_type: SpaceType;
      };
      response: TodayAvailabilityResponse;
      error: CommonError;
    };
  };
  POST: {
    //スタッフ用複数予約一括キャンセル
    StaffBulkCancelReservations: {
      path: '/api/spaces/staff/reservations/bulk_cancel';
      parameters: {
        reservation_nos: string[];
      };
      response: {
        succeeded_count: number;
        failed_count: number;
        errors: Array<{
          reservation_no: string;
          error: string;
        }>;
      };
      error: CommonError;
    };
    //ユーザー登録（仮登録）
    UserRegistration: {
      path: '/api/auth/registration';
      parameters: {
        family_name_jp: string;
        first_name_jp: string;
        email: string;
        membership_type: MembershipType;
      };
      response: {
        data:
          | {
              email: string;
              already_exist_account: boolean;
              signup_session_id: string;
            }
          | {
              initial_login_pattern: InitialLoginPattern;
            };
      };
      error: CommonError;
    };
    //ユーザー登録完了（本登録）
    UserRegistrationComplete: {
      path: '/api/auth/activation';
      parameters: {
        email: string;
        authorization_code: string;
        password: string;
        signup_session_id: string;
        membership_type: MembershipType;
      };
      response: {
        data: {
          jwt: string;
          token_type: string;
          expires_at: number;
          role: UserRole;
        };
      };
      error: CommonError;
    };
    //Workhub顧客なしユーザーの登録（初期セットアップフロー）
    CreateContract: {
      path: '/api/auth/no_workhub_customer_registration';
      parameters: {
        email: string;
        family_name_jp: string;
        first_name_jp: string;
        membership_type: MembershipType;
      };
      response: {
        data: {
          success: boolean;
          email: string;
        };
      };
      error: CommonError;
    };
    //パスワード再設定メール送信
    PasswordResetEmail: {
      path: '/api/password/reset_request';
      parameters: {
        email: string;
      };
      response: Record<string, never>; // 204 No Content - レスポンスボディなし
      error: CommonError;
    };
    //ユーザーログイン
    UserLogin: {
      path: '/api/auth/session';
      parameters: {
        email: string;
        password: string;
        membership_type?: MembershipType;
      };
      response: {
        data:
          | {
              jwt: string;
              expires_at: number;
              token_type: string;
              role: UserRole;
            }
          | {
              initial_login_pattern: InitialLoginPattern;
            };
      };
      error: CommonError;
    };
    //スタッフログイン
    StaffLogin: {
      path: '/api/auth/staff/session';
      parameters: {
        email: string;
        password: string;
      };
      response: {
        data: {
          jwt: string;
          expires_at: number;
          token_type: string;
          role: UserRole;
        };
      };
      error: CommonError;
    };
    //共有端末ログイン
    SharedDeviceLogin: {
      path: '/api/auth/shared_device/session';
      parameters: {
        email: string;
        password: string;
      };
      response: {
        data: {
          jwt: string;
          expires_at: number;
          token_type: string;
          role: UserRole;
        };
      };
      error: CommonError;
    };
    //トークンリフレッシュ
    TokenRefresh: {
      path: '/api/auth/refresh';
      parameters: Record<string, never>;
      response: {
        status: string;
        data: {
          jwt: string;
          expires_at: number;
          token_type: string;
          role: UserRole;
        };
      };
      error: CommonError;
    };
    //共有端末から予約作成
    CreateSharedDeviceReservation: {
      path: '/api/spaces/shared_device/reservations';
      parameters: {
        reservation: {
          last_name: string;
          first_name: string;
          email: string;
          start_time: string;
          end_time: string;
          space_type: string;
        };
      };
      response: {
        message: string;
        reservation: {
          id: number;
          reservation_no: string;
          pass_code: string;
          last_name: string;
          first_name: string;
          start_time: string;
          end_time: string;
          date: string;
          day_of_week: string;
          airport: 'haneda' | 'narita';
          terminal:
            | 'terminal1'
            | 'terminal2'
            | 'terminal3'
            | 'satellite2'
            | 'satellite5';
          flight_type: 'domestic' | 'international';
          lounge: 'ana_lounge' | 'suite_lounge' | 'common';
          floor: number;
          device_floor: number;
          space_no: number;
          space_type: 'shower_room' | 'private_room';
          key_type: 'physical_key_only' | 'digital';
        };
      };
      error: CommonError;
    };
    //通常の予約作成
    CreateReservation: {
      path: '/api/spaces/reservations';
      parameters: {
        reservation: {
          start_time: string;
          end_time: string;
          area_ids: number[];
          space_type: string;
          space_id?: number; // ブース予約時のスペース指定（オプション）
        };
      };
      response: {
        reservation: {
          id: number;
          reservation_no: string;
          pass_code: string;
          last_name: string;
          first_name: string;
          email: string;
          start_time: string;
          end_time: string;
          date: string;
          day_of_week: string;
          airport: 'haneda' | 'narita';
          terminal:
            | 'terminal1'
            | 'terminal2'
            | 'terminal3'
            | 'satellite2'
            | 'satellite5';
          flight_type: 'domestic' | 'international';
          lounge: 'ana_lounge' | 'suite_lounge' | 'common';
          floor: number;
          space_no: number;
          space_name: string;
          space_type: 'shower_room' | 'private_room';
          key_type: 'physical_key_only' | 'digital';
        };
      };
      error: CommonError;
    };
  };
  PUT: {
    //パスワードリセット完了
    PasswordResetComplete: {
      path: '/api/password';
      parameters: {
        reset_token: string;
        password: string;
        agreed_to_terms: boolean;
      };
      response: {
        data: {
          reset_pattern: 'initial' | 'normal';
          email?: string;
          first_name_jp?: string;
          family_name_jp?: string;
          jwt?: string;
          expires_at?: number;
          token_type?: string;
          role?: UserRole;
        };
      };
      error: CommonError;
    };
  };
  DELETE: {
    //予約キャンセル
    CancelReservation: {
      path: '/api/spaces/reservations/:id';
      parameters: {
        id: number;
      };
      response: Record<string, never>; // 204 No Content - レスポンスボディなし
      error: CommonError;
    };
  };
};

type AuthError = {
  status_code: 403;
};

type ServerError = {
  status_code: 500;
  message: string;
};

type CommonError = AuthError | ServerError;
